import { DesignActionType, FontRole, TextStyle as ITextStyle } from 'types'
import { useButton } from '@react-aria/button'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { forwardRef } from 'react'
import { useObjectRef } from '@react-aria/utils'
import { TextBoxShape, TextBoxShapeProps } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/Types'
import { assignWith } from 'lodash'
import { alignShapesToFrame, getCurrentFrame } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { createShapeId, ShapeUtil } from '@tldraw/tldraw'
import { TextStylePreview } from './TextStylePreview'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import Styles from './BrandKitTextStyles.module.scss'
import { useCompleteDesignAction } from 'components/Design/utils/useCompleteDesignAction'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'

interface Props {
  /**
   * The text style to display.
   */
  textStyle: ITextStyle
  /**
   * The label to display for the text style.
   */
  label: string
  /**
   * This is a callback that is called when the user clicks on the text style.
   * If this is not provided, the text style will be added to the canvas.
   */
  onPress?: (props: Partial<TextBoxShapeProps>) => void
}

// This is a fixed mapping of font roles to font sizes
// that are used to scale the font size based on the height of the frame.
const FONT_SIZE_MAPPING: Record<FontRole, number> = {
  [FontRole.Title]: 32,
  [FontRole.Subtitle]: 24,
  [FontRole.Body]: 14,
}

// This is a fixed scaling factor that is used to scale the font size based on
// the height of the frame.
const FONT_SCALING_FACTOR = 300

const scaleFontSize = (fontSize: number, height: number) => {
  return (fontSize * height) / FONT_SCALING_FACTOR
}

export const TextStyle = observer(
  forwardRef<HTMLDivElement, Props>(({ textStyle, label, onPress }, fRef) => {
    const editor = useEditor()
    const ref = useObjectRef(fRef)

    // Safely get textBoxShapeutil. It's possible to be here before the shapes are loaded
    let textBoxShapeutil: ShapeUtil<TextBoxShape> | undefined = undefined
    try {
      textBoxShapeutil = editor.getShapeUtil<TextBoxShape>('textBox')
    } catch {}

    const completeActionMutation = useCompleteDesignAction()
    const { drawersStore } = useRootStore()
    const { style } = textStyle

    const { buttonProps } = useButton(
      {
        elementType: 'div',
        onPress: () => {
          const frame = getCurrentFrame(editor)
          const props = assignWith(
            textBoxShapeutil?.getDefaultProps(),
            {
              fontFamily: style.font_family,
              fontSize: scaleFontSize(FONT_SIZE_MAPPING[textStyle.font_role], frame.props.h),
              fontWeight: style.font_weight,
              italic: style.italic,
              uppercase: style.uppercase,
              underline: style.underline,
              strikethrough: style.strikethrough,
            },
            (defaultValue, kitValue) => {
              if (kitValue === null || kitValue === undefined) {
                return defaultValue
              }
            },
          )
          if (onPress) {
            onPress(props)
            return
          }

          editor.batch(() => {
            const id = createShapeId()
            editor.createShape({
              id,
              type: 'textBox',
              parentId: frame.id,
              props: {
                ...props,
                text: label,
              },
            })
            alignShapesToFrame(editor, [id], 'center-horizontal')
            alignShapesToFrame(editor, [id], 'center-vertical')
            editor.select(id)
          })
          completeActionMutation.mutate({ action: DesignActionType.AddText })
          drawersStore.closeDrawer()
        },
      },
      ref,
    )

    if (!textBoxShapeutil) {
      return null
    }

    const MAX_FONT_SIZE = 24
    const fontSizes = [MAX_FONT_SIZE]
    if (style.font_size) {
      fontSizes.push(parseInt(style.font_size.toString()))
    }

    return (
      <div ref={ref} {...buttonProps} style={{ outline: 'none' }}>
        <TextStylePreview
          style={{
            fontSize: style.font_size && Math.min(...fontSizes),
            fontFamily: `'${style.font_family}'`,
            fontWeight: style.font_weight,
            fontStyle: style.italic ? 'italic' : undefined,
            textTransform: style.uppercase ? 'uppercase' : undefined,
            textDecoration: [style.underline && 'underline', style.strikethrough && 'line-through']
              .filter(Boolean)
              .join(' '),
          }}
        >
          <span className={Styles.textStyleLabel}>{label}</span>
          <span className={Styles.addTextStyleBadge}>Add</span>
        </TextStylePreview>
        {style.font_family && (
          <>
            <GoogleFontLoader family={style.font_family} />
            <BlazeFontLoader name={style.font_family} />
          </>
        )}
      </div>
    )
  }),
)
